import { apiRequest } from "@/lib/api/client";
import { ApiError } from "@/lib/api/errors";
import { login as authLogin, logout as authLogout } from "@/lib/auth";
import type {
  AlertAction,
  ApiAdapter,
  AuditDetailItem,
  AuditTrailEntry,
  DashboardPeriod,
  DashboardStats,
  HistoryRow,
  ListAlertsParams,
  ListScreenshotsParams,
  ListUsersParams,
  PaginatedResult,
  Permission,
  Role,
  Screenshot,
} from "@/types/api-types";
import type { Alert, Video } from "@/types/alerts-type";
import type { User } from "@/types/users-type";

const API_PREFIX = "/v1";

type RawRole = {
  id?: string | number;
  name?: string;
  code?: string;
  status?: string;
  users?: number;
  userCount?: number;
  description?: string;
  permissions?: unknown[] | number;
};

type RawPermission = {
  id?: string;
  name?: string;
  resource?: string;
  action?: string;
};

function mapPermission(raw: RawPermission): Permission {
  return {
    id: raw.id ?? "",
    name: raw.name ?? "Unnamed permission",
    resource: raw.resource ?? "-",
    action: raw.action ?? "-",
  };
}

function mapRole(raw: RawRole): Role {
  const rawPermissions = Array.isArray(raw.permissions)
    ? (raw.permissions as RawPermission[])
    : [];

  const permissionDetails = rawPermissions.map(mapPermission);

  const permissionsCount = permissionDetails.length > 0
    ? permissionDetails.length
    : typeof raw.permissions === "number"
      ? raw.permissions
      : 0;

  return {
    id: raw.id ?? "",
    name: raw.name ?? "Unknown",
    code: raw.code ?? raw.name ?? "-",
    status: raw.status ?? "active",
    description: raw.description,
    users:
      typeof raw.users === "number"
        ? raw.users
        : typeof raw.userCount === "number"
          ? raw.userCount
          : 0,
    permissions: permissionsCount,
    permissionDetails,
  };
}

function normalizeRolePayload(payload: unknown): Role | null {
  if (!payload || typeof payload !== "object") return null;

  const root = payload as Record<string, unknown>;

  if (root.id !== undefined && typeof root.name === "string") {
    return mapRole(root as RawRole);
  }

  const data = root.data;
  if (data && typeof data === "object") {
    const dataObj = data as Record<string, unknown>;
    if (dataObj.role && typeof dataObj.role === "object") {
      return mapRole(dataObj.role as RawRole);
    }
    if (dataObj.id !== undefined && typeof dataObj.name === "string") {
      return mapRole(dataObj as RawRole);
    }
  }

  return null;
}

function normalizeRolesPayload(payload: unknown): Role[] {
  const candidateArrays: unknown[] = [payload];

  if (payload && typeof payload === "object") {
    const root = payload as Record<string, unknown>;
    candidateArrays.push(
      root.roles,
      root.data,
      root.record,
      root.records,
      root.items,
      root.rows,
    );

    if (root.data && typeof root.data === "object") {
      const dataObj = root.data as Record<string, unknown>;
      candidateArrays.push(
        dataObj.roles,
        dataObj.data,
        dataObj.record,
        dataObj.records,
        dataObj.items,
        dataObj.rows,
      );
    }
  }

  const firstArray = candidateArrays.find((value) => Array.isArray(value));
  return Array.isArray(firstArray)
    ? (firstArray as RawRole[]).map(mapRole)
    : [];
}

function normalizePermissionsPayload(payload: unknown): Permission[] {
  const candidateArrays: unknown[] = [payload];

  if (payload && typeof payload === "object") {
    const root = payload as Record<string, unknown>;
    candidateArrays.push(
      root.permissions,
      root.data,
      root.record,
      root.records,
      root.items,
      root.rows,
    );

    if (root.data && typeof root.data === "object") {
      const dataObj = root.data as Record<string, unknown>;
      candidateArrays.push(
        dataObj.permissions,
        dataObj.data,
        dataObj.record,
        dataObj.records,
        dataObj.items,
        dataObj.rows,
      );
    }
  }

  const firstArray = candidateArrays.find((value) => Array.isArray(value));
  return Array.isArray(firstArray)
    ? (firstArray as RawPermission[]).map(mapPermission)
    : [];
}

export const httpAdapter: ApiAdapter = {
  auth: {
    async login() {
      await authLogin();
    },
    async logout() {
      await authLogout();
    },
  },
  users: {
    list(params: ListUsersParams) {
      return apiRequest<PaginatedResult<User>>(`${API_PREFIX}/users`, {
        params: {
          search: params.search,
          page: params.page,
          pageSize: params.pageSize,
        },
      });
    },
  },
  roles: {
    async list() {
      try {
        const payload = await apiRequest<unknown>("/api/roles");
        return normalizeRolesPayload(payload);
      } catch {
        // Fallback for environments still serving roles under the v1 prefix.
        const payload = await apiRequest<unknown>(`${API_PREFIX}/roles`);
        return normalizeRolesPayload(payload);
      }
    },
    async getById(id: string) {
      const encodedId = encodeURIComponent(id);

      try {
        const payload = await apiRequest<unknown>(`/api/roles/${encodedId}`);
        const role = normalizeRolePayload(payload);
        if (role) return role;
      } catch {
        const payload = await apiRequest<unknown>(
          `${API_PREFIX}/roles/${encodedId}`,
        );
        const role = normalizeRolePayload(payload);
        if (role) return role;
      }

      throw new ApiError("We couldn't find this role.", 404);
    },
    async updatePermissions(id: string, permissionIds: string[]) {
      const encodedId = encodeURIComponent(id);
      const body = { permissionIds };

      try {
        await apiRequest(`/api/roles/${encodedId}/permissions`, {
          method: "PATCH",
          body,
        });
      } catch {
        await apiRequest(`${API_PREFIX}/roles/${encodedId}/permissions`, {
          method: "PATCH",
          body,
        });
      }
    },
  },
  permissions: {
    async list() {
      try {
        const payload = await apiRequest<unknown>("/api/permissions");
        return normalizePermissionsPayload(payload);
      } catch {
        const payload = await apiRequest<unknown>(`${API_PREFIX}/permissions`);
        return normalizePermissionsPayload(payload);
      }
    },
  },
  alerts: {
    list(params?: ListAlertsParams) {
      return apiRequest<Alert[]>(`${API_PREFIX}/alerts`, {
        params: {
          videoId: params?.videoId,
          status: params?.status,
        },
      });
    },
    updateStatus(id: number, action: AlertAction) {
      return apiRequest<Alert>(`${API_PREFIX}/alerts/${id}`, {
        method: "PATCH",
        body: { action },
      });
    },
  },
  videos: {
    list() {
      return apiRequest<Video[]>(`${API_PREFIX}/videos`);
    },
    getById(id: number) {
      return apiRequest<Video | null>(`${API_PREFIX}/videos/${id}`);
    },
  },
  screenshots: {
    list(params?: ListScreenshotsParams) {
      return apiRequest<Screenshot[]>(`${API_PREFIX}/screenshots`, {
        params: {
          videoId: params?.videoId,
          page: params?.page,
          pageSize: params?.pageSize,
        },
      });
    },
  },
  auditTrail: {
    list() {
      return apiRequest<AuditTrailEntry[]>(`${API_PREFIX}/audit-trail`);
    },
    getDetail(videoName: string) {
      return apiRequest<AuditDetailItem[]>(
        `${API_PREFIX}/audit-trail/detail`,
        { params: { videoName } },
      );
    },
  },
  history: {
    list() {
      return apiRequest<HistoryRow[]>(`${API_PREFIX}/history`);
    },
  },
  dashboard: {
    getStats(period: DashboardPeriod) {
      return apiRequest<DashboardStats>(`${API_PREFIX}/dashboard/stats`, {
        params: { period },
      });
    },
    // getActivity(period: DashboardPeriod) {
    //   return apiRequest<DashboardActivity[]>(
    //     `${API_PREFIX}/dashboard/activity`,
    //     { params: { period } },
    //   );
    // },
  },
  uploads: {
    uploadVideos(files: File[]) {
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));
      return apiRequest<{ jobId: string }>(`${API_PREFIX}/videos/upload`, {
        method: "POST",
        body: formData,
      });
    },
  },
};
